import { addDays, startOfDay } from "date-fns";
import {
  SELECT_ACTIVITY_ORG,
  SET_ACTIVITY_FLAGGED_ONLY,
  SET_ACTIVITY_SORT,
  FETCH_ACTIVITY_FLEET_START,
  FETCH_ACTIVITY_FLEET,
  FETCH_ACTIVITY_FLEET_FAILURE,
  SET_ACTIVITY_FLEET_ROW,
  SET_ACTIVITY_SEARCH,
  SELECT_ACTIVITY_DEVICE,
  FETCH_ACTIVITY_ALERTS_START,
  FETCH_ACTIVITY_ALERTS,
  FETCH_ACTIVITY_ALERTS_FAILURE,
  SET_ACTIVITY_ALERT_TYPE,
  MARK_ACTIVITY_ALERTS_READ,
  SET_ACTIVITY_UPDATED_RANGE,
  SET_ACTIVITY_STATE_MACHINE,
} from "./types";

import {
  displaySuccessNotification,
  displayErrorNotification,
} from "./notifications";
import { decrementOrgUnreadMaintenanceCount } from "./users";

// How many rows a foreground fetch asks for, and so how many the user gets in
// one page. A background refresh overrides it with the number of rows already
// loaded, so a paged-out list refreshes in place instead of collapsing to page 1.
const PAGE_SIZE = 50;

export const selectActivityOrg = (organizationId) => async (dispatch) => {
  dispatch({ type: SELECT_ACTIVITY_ORG, payload: { organizationId } });
};

export const setActivityFlaggedOnly = (flaggedOnly) => async (dispatch) => {
  dispatch({ type: SET_ACTIVITY_FLAGGED_ONLY, payload: { flaggedOnly } });
};

// null = the default flagged-first, then-staleest-first sort; "updated_asc"/
// "updated_desc" take over once the user clicks the Updated column header
export const setActivitySort = (sort) => async (dispatch) => {
  dispatch({ type: SET_ACTIVITY_SORT, payload: { sort } });
};

export const setActivitySearch = (search) => async (dispatch) => {
  dispatch({ type: SET_ACTIVITY_SEARCH, payload: { search } });
};

export const setActivityUpdatedRange =
  ({ from, to }) =>
  async (dispatch) => {
    dispatch({ type: SET_ACTIVITY_UPDATED_RANGE, payload: { from, to } });
  };

export const setActivityStateMachine = (stateMachine) => async (dispatch) => {
  dispatch({ type: SET_ACTIVITY_STATE_MACHINE, payload: { stateMachine } });
};

export const fetchActivityFleet =
  (
    sendOrganizationRequest,
    {
      organizationId,
      flaggedOnly,
      search,
      sort,
      updatedFrom,
      updatedTo,
      stateMachine,
      env,
      cursor,
      limit,
      background,
    }
  ) =>
  async (dispatch, getState) => {
    const version = background
      ? getState()?.activityMonitor?.optimisticVersion
      : undefined;
    if (!background) {
      dispatch({ type: FETCH_ACTIVITY_FLEET_START });
    }
    const params = { limit: limit || PAGE_SIZE };
    if (organizationId) {
      params.organizationId = organizationId;
    } else {
      params.includeUnassigned = true;
    }
    if (flaggedOnly) {
      params.flagged = true;
    }
    if (search) {
      params.q = search;
    }
    if (sort) {
      params.sort = sort;
    }
    if (updatedFrom) {
      params.updatedSince = updatedFrom;
    }
    if (updatedTo) {
      params.updatedUntil = startOfDay(
        addDays(new Date(updatedTo), 1)
      ).getTime();
    }
    if (stateMachine) {
      params.stateMachine = stateMachine;
    }
    if (env) {
      params.env = env;
    }
    if (cursor) {
      params.cursor = cursor;
    }
    try {
      const res = await sendOrganizationRequest("get")({
        url: "/organization/fleet",
        params,
      });
      dispatch({
        type: FETCH_ACTIVITY_FLEET,
        payload: {
          items: res.data.items,
          nextCursor: res.data.nextCursor,
          count: res.data.count,
          append: !!cursor,
          organizationId,
          flaggedOnly,
          search,
          sort,
          updatedFrom,
          updatedTo,
          stateMachine,
          version,
        },
      });
    } catch (err) {
      if (!background) {
        dispatch(displayErrorNotification("Failed to load device fleet"));
      }
      dispatch({
        type: FETCH_ACTIVITY_FLEET_FAILURE,
        payload: { background },
      });
    }
  };

// refreshes one row in place (e.g. after mark-read) instead of refetching the page
export const refreshActivityFleetRow =
  (sendOrganizationRequest, deviceId, env) => async (dispatch) => {
    try {
      // `q` is an unanchored substring match over deviceId OR hardwareId, so it
      // can return rows that are not this device - another device's hardware id
      // containing this uuid would match too. Asking for a small window rather
      // than one row keeps the exact-match find below meaningful: with limit 1
      // a single colliding row would push the real one out and the refresh
      // would silently do nothing.
      const params = { q: deviceId, limit: 10 };
      if (env) {
        params.env = env;
      }
      const res = await sendOrganizationRequest("get")({
        url: "/organization/fleet",
        params,
      });
      const row = res.data.items.find((item) => item.deviceId === deviceId);
      if (row) {
        dispatch({ type: SET_ACTIVITY_FLEET_ROW, payload: { row } });
      }
    } catch (err) {
      // the row's unreadMaintenanceCount stays stale until the next full fleet fetch
    }
  };

export const selectActivityDevice = (device) => async (dispatch) => {
  dispatch({ type: SELECT_ACTIVITY_DEVICE, payload: { device } });
};

export const fetchActivityAlerts =
  (
    sendOrganizationRequest,
    deviceUuid,
    { alertType, cursor, limit, background }
  ) =>
  async (dispatch, getState) => {
    const version = background
      ? getState()?.activityMonitor?.optimisticVersion
      : undefined;
    if (!background) {
      dispatch({ type: FETCH_ACTIVITY_ALERTS_START });
    }
    const params = { alertType, limit: limit || PAGE_SIZE };
    if (cursor) {
      params.cursor = cursor;
    }
    try {
      const res = await sendOrganizationRequest("get")({
        url: `/organization/devices/${deviceUuid}/alerts`,
        params,
      });
      dispatch({
        type: FETCH_ACTIVITY_ALERTS,
        payload: {
          deviceUuid,
          alertType,
          items: res.data.items,
          nextCursor: res.data.nextCursor,
          count: res.data.count,
          append: !!cursor,
          version,
        },
      });
    } catch (err) {
      if (!background) {
        dispatch(displayErrorNotification("Failed to load device alerts"));
      }
      dispatch({
        type: FETCH_ACTIVITY_ALERTS_FAILURE,
        payload: { background },
      });
    }
  };

export const setActivityAlertType = (alertType) => async (dispatch) => {
  dispatch({ type: SET_ACTIVITY_ALERT_TYPE, payload: { alertType } });
};

// { organizationId, alertType } drives the optimistic badge decrement below and
// is never sent over the wire - body is posted verbatim, unchanged.
export const markActivityAlertsRead =
  (
    sendOrganizationRequest,
    deviceUuid,
    body,
    { organizationId, alertType } = {}
  ) =>
  async (dispatch, getState) => {
    let readResponse;
    try {
      readResponse = await sendOrganizationRequest("post")({
        url: `/organization/devices/${deviceUuid}/alerts/read`,
        data: body,
      });
    } catch (err) {
      dispatch(displayErrorNotification("Failed to mark alerts read"));
      return;
    }
    dispatch({
      type: MARK_ACTIVITY_ALERTS_READ,
      payload: {
        deviceUuid,
        ids: body.ids,
        all: body.all,
        alertType: body.alertType,
      },
    });
    dispatch(
      refreshActivityFleetRow(
        sendOrganizationRequest,
        deviceUuid,
        getState()?.activityMonitor?.env
      )
    );
    if (organizationId && alertType === "maintenance") {
      dispatch(
        decrementOrgUnreadMaintenanceCount(
          organizationId,
          readResponse?.data?.modified || 0
        )
      );
    }
    if (body.all) {
      dispatch(displaySuccessNotification("Marked all alerts read"));
    }
  };
